import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import { Profile } from "../../../stores/ProfileStore/Types";
import { CurrentUser } from "../../../stores/CurrentUserStore/Types";
import Subscription from "./Subscription";
import FunfactModal from "./FunfactModal";
import HomeSky, { ShootingStar } from "./HomeSky";
import { ProfileSidebarView } from "../ProfileSidebar";
import DoNext from "./DoNext";
import ProfileStore from "../../../stores/ProfileStore";
import SpiritTeacherQuiz from "../../AllCourses/components/SpiritTeacherQuiz";

interface Props {
  profileResource: Profile;
  profilePicture: string;
  currentUser: CurrentUser;
  profileStore: ProfileStore;
  getCurrentUser: () => void;
  showProfileSidebar: boolean;
  handleShowProfileSidebar: (value: boolean, view?: ProfileSidebarView) => void;
  handleShowLoggedInSideBar: () => void;
}

interface SkyStar {
  className: string;
  left: string;
  top: string;
  animationDuration: string;
  animationDelay: string;
}

const eggLines = [
  { dp: "Don't panic.", tw: "and always know where your towel is" },
  { dp: "42.", tw: "still the only answer that fits" },
];

const sky42Segments = [
  [60, 6, 60, 97],
  [58, 10, 10, 66],
  [10, 66, 62, 66],
  [110, 42, 114, 24],
  [114, 24, 134, 13],
  [134, 13, 156, 20],
  [156, 20, 160, 40],
  [160, 40, 135, 62],
  [135, 62, 110, 88],
  [110, 90, 176, 90],
];

const createSeededSky42Random = () => {
  let seed = 0;

  return () => {
    seed += 1;
    const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return value - Math.floor(value);
  };
};

const createSky42StarCluster = () => {
  const random = createSeededSky42Random();
  const strokeHalfWidth = 9;
  const stationSpacing = 2.4;

  return sky42Segments.reduce((allStars, [x0, y0, x1, y1]): SkyStar[] => {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const length = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = -dy / length;
    const py = dx / length;
    const steps = Math.max(1, Math.round(length / stationSpacing));
    const stars: SkyStar[] = [];

    for (let i = 0; i <= steps; i += 1) {
      for (let m = 0; m < 2; m += 1) {
        const t = Math.min(
          1,
          Math.max(0, (i + (random() * 0.9 - 0.45)) / steps),
        );
        let offset =
          ((random() + random() + random() - 1.5) / 1.5) * strokeHalfWidth;

        if (random() < 0.08) {
          offset *= 1.6;
        }

        const recipe = random();
        const sizeClass = recipe < 0.34 ? "s2" : recipe < 0.67 ? "s1" : "s3";
        const brightClass = random() < 0.08 ? "b" : sizeClass;
        const flicker = random();
        const motionClass = flicker < 0.4 ? "tw" : flicker < 0.72 ? "dm" : "";

        stars.push({
          className: ["star", brightClass, motionClass]
            .filter(Boolean)
            .join(" "),
          left: `${(((x0 + dx * t + px * offset + 10) / 210) * 100).toFixed(
            2,
          )}%`,
          top: `${(((y0 + dy * t + py * offset + 10) / 122) * 100).toFixed(2)}%`,
          animationDuration: `${(2.8 + random() * 3.4).toFixed(1)}s`,
          animationDelay: `${(random() * 5).toFixed(1)}s`,
        });
      }
    }

    return allStars.concat(stars);
  }, [] as SkyStar[]);
};

const sky42Stars = createSky42StarCluster();

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const skyClickIgnoreSelector = ".welcome-card, a, button, [role='button']";
const ambientStarMinDelay = 45000;
const ambientStarDelayRange = 45000;

const Sky42: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    className="sky-42"
    id="quizTrigger42"
    type="button"
    aria-label="A 42 hidden in the stars. Open the quiz: which 42courses brain is yours?"
    onClick={onClick}
  >
    <span className="sky42-field" id="sky42g" aria-hidden="true">
      {sky42Stars.map((star, index) => (
        <span
          key={index}
          className={star.className}
          style={{
            left: star.left,
            top: star.top,
            animationDuration: star.animationDuration,
            animationDelay: star.animationDelay,
          }}
        />
      ))}
    </span>
  </button>
);

const Landing: React.FC<Props> = ({
  profileResource,
  profilePicture,
  showProfileSidebar,
  handleShowProfileSidebar,
  currentUser,
}) => {
  const [showFunFactModal, setShowFunFactModal] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [shootingStars, setShootingStars] = useState<ShootingStar[]>([]);
  const [showEggMessage, setShowEggMessage] = useState(false);
  const [eggLine, setEggLine] = useState(eggLines[0]);
  const headerRef = useRef<HTMLElement | null>(null);
  const eggMessageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shootingStarTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const ambientStarTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shootingStarId = useRef(0);
  const lastEggLineIndex = useRef(-1);

  const fireShootingStar = (left: number, top: number) => {
    if (prefersReducedMotion()) {
      return;
    }

    shootingStarId.current += 1;

    const direction = Math.random() < 0.5 ? -1 : 1;
    const angle = 8 + Math.random() * 20;
    const distance = 260 + Math.random() * 120;
    const radians = (angle * Math.PI) / 180;
    const rotation =
      (Math.atan2(-Math.sin(radians), direction * Math.cos(radians)) * 180) /
      Math.PI;
    const id = shootingStarId.current;

    setShootingStars((stars) =>
      stars.concat({
        id,
        left,
        top,
        rotation: `${rotation.toFixed(1)}deg`,
        distance: `${distance.toFixed(0)}px`,
      }),
    );

    const timer = setTimeout(() => {
      setShootingStars((stars) => stars.filter((star) => star.id !== id));
      shootingStarTimers.current = shootingStarTimers.current.filter(
        (activeTimer) => activeTimer !== timer,
      );
    }, 1600);

    shootingStarTimers.current.push(timer);
  };

  const fireAmbientShootingStar = () => {
    const header = headerRef.current;

    if (!header) {
      return;
    }

    const rect = header.getBoundingClientRect();

    fireShootingStar(
      rect.width * 0.15 + Math.random() * rect.width * 0.7,
      20 + Math.random() * 40,
    );
  };

  useEffect(() => {
    // ---------- A rare, unbidden shooting star ----------
    // Every so often one drifts across on its own, so even a still page keeps a
    // small pulse of life. Rare and faint by design; change the cadence here if
    // you want more. Skipped entirely for reduced-motion users.
    const scheduleAmbientStar = () => {
      if (prefersReducedMotion()) {
        return;
      }

      ambientStarTimer.current = setTimeout(
        () => {
          fireAmbientShootingStar();
          scheduleAmbientStar();
        },
        ambientStarMinDelay + Math.random() * ambientStarDelayRange,
      );
    };

    scheduleAmbientStar();

    return () => {
      if (eggMessageTimer.current) {
        clearTimeout(eggMessageTimer.current);
      }

      if (ambientStarTimer.current) {
        clearTimeout(ambientStarTimer.current);
      }

      shootingStarTimers.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const handleShowFunFactModal = () => {
    setShowFunFactModal(true);
  };

  const handleHideFunFactModal = () => {
    setShowFunFactModal(false);
  };

  const showEggLine = () => {
    let lineIndex = Math.floor(Math.random() * eggLines.length);

    if (eggLines.length > 1) {
      while (lineIndex === lastEggLineIndex.current) {
        lineIndex = Math.floor(Math.random() * eggLines.length);
      }
    }

    lastEggLineIndex.current = lineIndex;
    setEggLine(eggLines[lineIndex]);
    setShowEggMessage(true);

    if (eggMessageTimer.current) {
      clearTimeout(eggMessageTimer.current);
    }

    eggMessageTimer.current = setTimeout(() => {
      setShowEggMessage(false);
      eggMessageTimer.current = null;
    }, 3600);
  };

  // Click anywhere in the sky (not the welcome card or its controls): a star
  // streaks out and the line pops up.
  const handleHeaderClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target;

    if (
      !(target instanceof Element) ||
      target.closest(skyClickIgnoreSelector)
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    fireShootingStar(event.clientX - rect.left, event.clientY - rect.top);
    showEggLine();
  };

  // The logo is a button (so excluded above); it fires a star from itself and
  // shows the line too. Keyboard activation fires from the logo's centre.
  const handleLogoEggClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const header = headerRef.current;

    if (header) {
      const logoRect = event.currentTarget.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      const keyboardActivation = event.detail === 0;
      const left =
        (keyboardActivation
          ? logoRect.left + logoRect.width / 2
          : event.clientX) - headerRect.left;
      const top =
        (keyboardActivation
          ? logoRect.top + logoRect.height / 2
          : event.clientY) - headerRect.top;

      fireShootingStar(left, top);
    }

    showEggLine();
  };

  const setShowProfileSidebar = (view: ProfileSidebarView) => {
    handleShowProfileSidebar(true, view);
  };

  const getInitials = () => {
    const parts =
      profileResource.name?.trim().split(/\s+/).filter(Boolean) || [];

    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    const fallbackName = parts[0] || profileResource.first_name || "";
    return `${fallbackName[0] || ""}${
      fallbackName[fallbackName.length - 1] || ""
    }`.toUpperCase();
  };

  const hasOngoingCourses = profileResource.enrolments?.ongoing > 0;
  return (
    <>
      <header
        className="home-header"
        ref={headerRef}
        onClick={handleHeaderClick}
      >
        <HomeSky shootingStars={shootingStars} />
        <Sky42 onClick={() => setShowQuiz(true)} />
        <div
          className={`sky-msg${showEggMessage ? " show" : ""}`}
          id="skyMsg"
          aria-live="polite"
        >
          <span className="dp">{eggLine.dp}</span>
          <span className="tw">{eggLine.tw}</span>
        </div>
        <div className="container">
          <button
            className="logo-center"
            id="logoEgg"
            type="button"
            aria-label="42courses"
            data-tip="42: the answer to life, the universe and everything"
            data-tip-wrap="true"
            data-tip-hover-only="true"
            onClick={handleLogoEggClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="4 37 596 158"
              role="img"
              aria-hidden="true"
            >
              <path
                fill="#ffffff"
                d="M214.32,123.21c0,16,11.42,28.19,27.06,28.19,16.77,0,24.7-11.83,25.73-21h-15a10.84,10.84,0,0,1-10.8,8c-7.82,0-12.35-6.17-12.35-15.22S233.46,108,241.28,108a10.84,10.84,0,0,1,10.8,8h15c-1-9.16-9-21-25.73-21C225.74,95,214.32,107.15,214.32,123.21Z"
              />
              <path
                fill="#ffffff"
                d="M300.6,151.4c17.29,0,28.3-12.86,28.3-28.19S317.89,95,300.6,95s-28.3,12.86-28.3,28.2S283.31,151.4,300.6,151.4Zm-13.69-28.19c0-9.68,6-15.34,13.69-15.34s13.68,5.66,13.68,15.34-6,15.33-13.68,15.33S286.91,132.88,286.91,123.21Z"
              />
              <path
                fill="#ffffff"
                d="M362.88,151.4c15.74,0,24.49-6.79,24.49-24.28V96.25H372.76v31.07c0,7.51-3.4,11-9.88,11s-9.88-3.5-9.88-11V96.25H338.39v30.87C338.39,144.61,347.14,151.4,362.88,151.4Z"
              />
              <path
                fill="#ffffff"
                d="M430.66,95.83s-1.23-.1-1.75-.1c-9.16,0-13.27,4.32-15.23,7.51v-7H400.2v53.92h14.62V123.31c0-9.88,4.42-14.1,13-14.1a17.22,17.22,0,0,1,2.88.31Z"
              />
              <path
                fill="#ffffff"
                d="M457.23,151.4c14.61,0,22.84-7.61,22.84-17.29,0-22.12-29.84-13.07-29.84-22.12,0-3,2.67-5.25,7.41-5.25,5.25,0,7.82,3.09,8.13,6.48h14.09c-.4-7.92-6-18.21-22.12-18.21-13.27,0-21.61,8-21.61,17.8,0,21.41,29.95,12.86,29.95,21.92,0,3-2.89,4.94-8.44,4.94-6.07,0-8.75-3.4-8.95-7H433.87C434.59,142.76,440.35,151.4,457.23,151.4Z"
              />
              <path
                fill="#ffffff"
                d="M539.86,127.42s.2-2,.2-4.32c0-15.95-10.8-28.09-26.75-28.09s-27.07,12.14-27.07,28.09c0,16.57,11.22,28.3,27.17,28.3,12.45,0,22.33-7.51,25.52-18.42H524.21c-1.75,3.91-6,5.87-10.39,5.87-7.3,0-12-4-13.07-11.43ZM513.2,106.74c6.79,0,11.22,4.12,12.25,10.19H501C502.19,110.75,506.52,106.74,513.2,106.74Z"
              />
              <path
                fill="#ffffff"
                d="M569.07,151.4c14.61,0,22.85-7.61,22.85-17.29,0-22.12-29.84-13.07-29.84-22.12,0-3,2.67-5.25,7.4-5.25,5.25,0,7.82,3.09,8.13,6.48h14.1c-.41-7.92-6-18.21-22.12-18.21-13.28,0-21.61,8-21.61,17.8,0,21.41,29.94,12.86,29.94,21.92,0,3-2.88,4.94-8.44,4.94-6.07,0-8.74-3.4-9-7H545.71C546.44,142.76,552.19,151.4,569.07,151.4Z"
              />
              <path
                fill="#ffffff"
                d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
              />
              <path
                fill="#ffffff"
                d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
              />
              <path
                fill="#2E2437"
                d="M68.9,149.18H80.76v-8.11h6.69v-10.2H80.76V97.61h-9.6l-23,36v7.43H68.9ZM61,131.54l8.86-14.71v14.71Z"
              />
              <path
                fill="#2E2437"
                d="M108.51,138.22v-.15l8-8.33c6.83-7.13,8.33-11.56,8.33-17.42,0-9.61-7.81-15.39-16.67-15.39s-14.48,4.43-17.56,9.76l8.63,6.23c2.25-3.3,4.88-4.8,8-4.8a5.32,5.32,0,0,1,5.4,5.55c0,3.68-2.32,6.91-6.38,11.19L91,141.3l.15,7.88h34.24v-11Z"
              />
            </svg>
          </button>
          <div className="welcome-card">
            <div className="welcome-id">
              <button
                className="welcome-avatar"
                type="button"
                id="welcomeAvatar"
                data-tip="Edit your photo"
                aria-label="Edit your photo"
                aria-controls="profile_home_sidebar"
                aria-expanded={showProfileSidebar}
                onClick={() => setShowProfileSidebar("settings")}
              >
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    className="user-image"
                    alt="profile picture"
                  />
                ) : (
                  getInitials()
                )}
                <span className="wa-edit" aria-hidden="true">
                  <svg>
                    <use href="#ln-camera" />
                  </svg>
                </span>
              </button>
              <div className="welcome-greet">
                <div className="hi">Welcome back</div>
                <div className="nm">{profileResource.name}</div>
                <div className="wxp">
                  <strong>{currentUser.xp}</strong> XP
                </div>
              </div>
              <button
                className="welcome-cog"
                type="button"
                id="welcomeCog"
                data-tip="Settings"
                aria-label="Settings"
                aria-controls="profile_home_sidebar"
                aria-expanded={showProfileSidebar}
                onClick={() => setShowProfileSidebar("settings")}
              >
                <svg aria-hidden="true">
                  <use href="#icon-cog" />
                </svg>
              </button>
            </div>
            <div className="welcome-stats">
              <a
                href="#"
                className="wstat"
                data-smooth-scroll-to="#profile-leaderboard"
              >
                <span className="wstat-ic rank">
                  <svg aria-hidden="true">
                    <use href="#ln-rank" />
                  </svg>
                </span>
                <span className="wstat-label">
                  <span className="lbl-full">
                    {" "}
                    {profileResource?.leaderboard_type?.name} Rank
                  </span>
                  <span className="lbl-short">Rank</span>
                </span>
                <span className="wstat-value">
                  {" "}
                  {profileResource?.leaderboard_type?.rank}
                </span>
              </a>
              <Link to="/achievements/badges" className="wstat">
                <span className="wstat-ic badges">
                  <svg aria-hidden="true">
                    <use href="#ln-badge" />
                  </svg>
                </span>
                <span className="wstat-label">Badges</span>
                <span className="wstat-value">
                  {profileResource.badge.awarded}
                </span>
              </Link>
              <a
                href="#"
                data-smooth-scroll-to="#certificates"
                className="wstat"
                id="wstatCerts"
              >
                <span className="wstat-ic certs">
                  <svg
                    aria-hidden="true"
                    className="ic-cert-gold"
                    viewBox="0 0 48 56"
                  >
                    <use href="#icon-cert-gold" />
                  </svg>
                </span>
                <span className="wstat-label">
                  <span className="lbl-full">Certificates</span>
                  <span className="lbl-short">Certs</span>
                </span>
                <span className="wstat-value">
                  {profileResource.enrolments.finished}
                </span>
              </a>
              <Subscription profileResource={profileResource} />
              <button
                className="wstat"
                id="wstatFun"
                type="button"
                onClick={handleShowFunFactModal}
              >
                <span className="wstat-ic fun">
                  <svg aria-hidden="true">
                    <use href="#ln-bulb" />
                  </svg>
                </span>
                <span className="wstat-label">Fun Fact</span>
                <span className="wstat-value">Tap me</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className="header-spacer"></div>

      <DoNext
        hasActiveEnrolments={hasOngoingCourses}
        userIsInUk={currentUser?.is_in_uk}
      />
      <FunfactModal
        funFact={profileResource.zen_pool}
        showFunFactModal={showFunFactModal}
        handleHideFunFactModal={handleHideFunFactModal}
      />
      <SpiritTeacherQuiz
        isOpen={showQuiz}
        onOpen={() => setShowQuiz(true)}
        onClose={() => setShowQuiz(false)}
      />
    </>
  );
};

export default observer(Landing);
